import '@treasury/presentation/components/tm-frequency';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { TmBaseComponent } from '../../tm-base.component';
import { summarizeFrequency } from '../tm-frequency.summarizer';
import type { Frequency } from '../tm-frequency.types';
import './tm-slider';

export type FrequencyEvent = CustomEvent<Frequency>;

export const tagName = 'tm-form-frequency';
@customElement(tagName)
export class TmFormFrequency extends TmBaseComponent {
    @property({ type: String })
    public dateLabel?: string;

    @property({ attribute: false })
    public minDate?: Date;

    @property({ attribute: false })
    public maxStartDate?: Date;

    @property({ type: Boolean })
    public disabled?: boolean;

    @property({ attribute: false })
    public loading: boolean = false;

    @property({ attribute: false })
    public isDateDisabled?: object;

    @state()
    private frequency?: Frequency;

    @state()
    private pendingFrequency?: Frequency;

    @state()
    private disableSubmitButton = false;

    public connectedCallback() {
        super.connectedCallback();
        this.addEventListener('apply-tm-slider', this.handleSliderApply);
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('apply-tm-slider', this.handleSliderApply);
    }

    protected render() {
        return html`
            <tm-form-row label="Frequency">
                <tm-slider
                    header="Select Frequency"
                    .value=${this.frequency ? 'Change frequency' : 'Select frequency'}
                    @selection=${this.handleSelection}
                    @cancel-tm-slider=${() => {
                        this.pendingFrequency = undefined;
                    }}
                    .disabled=${this.disabled}
                    .showControls=${true}
                    .disableSubmitButton=${this.disableSubmitButton}
                >
                    <tm-section class="p-4">
                        <tm-frequency
                            .frequency=${this.pendingFrequency || this.frequency}
                            dateLabel=${ifDefined(this.dateLabel)}
                            .minDate=${this.minDate}
                            .maxStartDate=${this.maxStartDate}
                            .isDateDisabled=${this.isDateDisabled}
                        ></tm-frequency>
                    </tm-section>
                </tm-slider>
                ${this.renderFrequencySummary()}
            </tm-form-row>
        `;
    }

    // Has to be in light dom so global custom border styles can affect it
    protected createRenderRoot() {
        return this;
    }

    private handleSelection(e: CustomEvent<Frequency | false>) {
        if (!e.detail) {
            this.disableSubmitButton = true;
            return;
        }
        this.disableSubmitButton = false;
        this.pendingFrequency = e.detail;
    }

    private handleSliderApply() {
        this.frequency = this.pendingFrequency;
        this.pendingFrequency = undefined;
        this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
        this.dispatchEvent(
            new CustomEvent('select-frequency', {
                detail: this.frequency,
                bubbles: true,
            }) as FrequencyEvent
        );
    }

    private renderFrequencySummary() {
        if (!this.frequency) {
            return nothing;
        }
        if (this.loading) {
            return html`<div slot="summary" class="flex justify-end py-4">
                <tm-loader inline></tm-loader>
            </div>`;
        }
        return html`<div slot="summary" class="text-sm py-4">
            ${summarizeFrequency(this.frequency)}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmFormFrequency;
    }
}
